import { Injectable } from '@angular/core';

import { environment } from '../../environments/environment';

export interface RuntimeLocation {
  origin: string;
  hostname: string;
}

function browserLocation(): RuntimeLocation | null {
  if (typeof window === 'undefined' || !window.location) return null;
  return { origin: window.location.origin, hostname: window.location.hostname };
}

function stripTrailingSlash(value: string): string {
  return String(value || '').trim().replace(/\/+$/, '');
}

function normalizeUrl(value: string, location: RuntimeLocation | null = browserLocation()): string {
  const raw = stripTrailingSlash(value);
  if (!raw) return location ? `${location.origin}/api` : '/api';
  if (/^https?:\/\//i.test(raw)) return raw;
  if (!location) return raw.startsWith('/') ? raw : `/${raw}`;
  return `${location.origin}${raw.startsWith('/') ? '' : '/'}${raw}`;
}

function parseUrl(value: string): URL | null {
  try { return new URL(value); } catch { return null; }
}

function isLocalHost(hostname: string): boolean {
  return ['localhost', '127.0.0.1', '::1'].includes(hostname);
}

function isAzureStaticWebAppHost(hostname: string): boolean {
  return hostname === 'azurestaticapps.net' || hostname.endsWith('.azurestaticapps.net');
}

function configuredDomainRoot(hostname: string): string {
  if (hostname.startsWith('api.staging.')) return hostname.slice('api.staging.'.length);
  if (hostname.startsWith('api.')) return hostname.slice('api.'.length);
  return hostname;
}

function isConfiguredDomainHost(current: string, configured: string): boolean {
  const root = configuredDomainRoot(configured);
  return current === configured || current === root || current.endsWith(`.${root}`);
}

export function resolveRuntimeApiUrl(
  configuredApiUrl = environment.apiUrl,
  location: RuntimeLocation | null = browserLocation(),
  production = environment.production,
): string {
  const configuredValue = normalizeUrl(configuredApiUrl, location);
  const configured = parseUrl(configuredValue);
  if (!location || !configured) return configuredValue;
  const currentHostname = location.hostname.toLowerCase();
  const configuredHostname = configured.hostname.toLowerCase();
  if (
    !isLocalHost(currentHostname) &&
    !isAzureStaticWebAppHost(currentHostname) &&
    !isConfiguredDomainHost(currentHostname, configuredHostname) &&
    production
  ) return `${stripTrailingSlash(location.origin)}/api`;
  return configuredValue;
}

export function apiRootFromApiUrl(apiUrl: string): string {
  return stripTrailingSlash(apiUrl).replace(/\/api\/?$/i, '');
}

export function resolveRuntimeAuthBaseUrl(
  configuredAuthBaseUrl = environment.authBaseUrl,
  location: RuntimeLocation | null = browserLocation(),
  runtimeApiUrl = resolveRuntimeApiUrl(environment.apiUrl, location, environment.production),
): string {
  if (location && apiRootFromApiUrl(runtimeApiUrl) === stripTrailingSlash(location.origin)) {
    return stripTrailingSlash(location.origin);
  }
  return normalizeUrl(configuredAuthBaseUrl, location);
}

export function joinApiUrl(apiUrl: string, path: string): string {
  const base = stripTrailingSlash(apiUrl);
  const suffix = String(path || '').trim();
  if (!suffix) return base;
  return `${base}${suffix.startsWith('/') ? '' : '/'}${suffix}`;
}

function authApiUrl(authBaseUrl: string): string { return joinApiUrl(authBaseUrl, '/api'); }

function runtimeLocation(runtimeApiUrl: string): RuntimeLocation | null {
  const parsed = parseUrl(runtimeApiUrl);
  return parsed ? { origin: parsed.origin, hostname: parsed.hostname } : browserLocation();
}

function normalizeRequestUrl(requestUrl: string, runtimeApiUrl: string): string {
  return normalizeUrl(requestUrl, runtimeLocation(runtimeApiUrl));
}

function matchesBase(url: string, base: string): boolean {
  return url === base || url.startsWith(`${base}/`) || url.startsWith(`${base}?`) || url.startsWith(`${base}#`);
}

export function isBackendRequestUrl(
  requestUrl: string,
  runtimeApiUrl = resolveRuntimeApiUrl(),
  configuredApiUrl = environment.apiUrl,
  runtimeAuthBaseUrl = resolveRuntimeAuthBaseUrl(),
): boolean {
  const location = runtimeLocation(runtimeApiUrl);
  const normalizedRequestUrl = normalizeRequestUrl(requestUrl, runtimeApiUrl);
  const candidates = new Set([
    stripTrailingSlash(runtimeApiUrl),
    stripTrailingSlash(normalizeUrl(configuredApiUrl, location)),
    stripTrailingSlash(normalizeUrl(authApiUrl(runtimeAuthBaseUrl), location)),
  ]);
  return [...candidates].some((base) => matchesBase(normalizedRequestUrl, base));
}

export function rewriteBackendRequestUrl(
  requestUrl: string,
  runtimeApiUrl = resolveRuntimeApiUrl(),
  configuredApiUrl = environment.apiUrl,
  runtimeAuthBaseUrl = resolveRuntimeAuthBaseUrl(),
): string | null {
  const runtimeBase = stripTrailingSlash(runtimeApiUrl);
  const location = runtimeLocation(runtimeApiUrl);
  const normalizedRequestUrl = normalizeRequestUrl(requestUrl, runtimeApiUrl);
  const configuredBase = stripTrailingSlash(normalizeUrl(configuredApiUrl, location));
  const authPath = stripTrailingSlash(normalizeUrl(joinApiUrl(authApiUrl(runtimeAuthBaseUrl), '/auth'), location));
  if (matchesBase(normalizedRequestUrl, authPath)) return normalizedRequestUrl;
  for (const base of [runtimeBase, configuredBase].filter((value, index, all) => value && all.indexOf(value) === index)) {
    if (matchesBase(normalizedRequestUrl, base)) return `${runtimeBase}${normalizedRequestUrl.slice(base.length)}`;
  }
  return null;
}

@Injectable({ providedIn: 'root' })
export class ApiUrlService {
  get apiUrl(): string { return resolveRuntimeApiUrl(); }
  get authBaseUrl(): string { return resolveRuntimeAuthBaseUrl(); }
  url(path: string): string { return joinApiUrl(this.apiUrl, path); }
  isBackendRequest(url: string): boolean {
    return isBackendRequestUrl(url, this.apiUrl, environment.apiUrl, this.authBaseUrl);
  }
  rewriteBackendUrl(url: string): string | null {
    return rewriteBackendRequestUrl(url, this.apiUrl, environment.apiUrl, this.authBaseUrl);
  }
}
